import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Language } from './types';
import { DEFAULT_LANGUAGE, STORAGE_KEY, TRANSLATIONS, type TranslationKey } from './translations';

type Vars = Record<string, string | number>;

type I18nContextValue = {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: TranslationKey, vars?: Vars) => string;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function format(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

function isLanguage(value: string): value is Language {
  return value === 'zh-TW' || value === 'en' || value === 'ja';
}

export const I18nProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [lang, setLangState] = useState<Language>(DEFAULT_LANGUAGE);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && isLanguage(stored)) setLangState(stored);
    } catch {
      // ignore (e.g. privacy mode)
    }
  }, []);

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const t = useCallback(
    (key: TranslationKey, vars?: Vars) => {
      const dict = TRANSLATIONS[lang] ?? TRANSLATIONS[DEFAULT_LANGUAGE];
      const fallback = TRANSLATIONS[DEFAULT_LANGUAGE];
      const raw = dict[key] ?? fallback[key] ?? key;
      return format(raw, vars);
    },
    [lang]
  );

  const value = useMemo<I18nContextValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

